import React, { useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Body, Card, CardBody } from '@panwds/react-ui';
import { useFetchIncidents } from '../../../services/api/incidents';
import { useFetchMaintenances } from '../../../services/api/maintenances';
import { useFetchAdvisories } from '../../../services/api/advisories';
import { EVENT_TYPES } from '../utils/eventTypes';
import { BORDERED_CARD_CLASS } from '../utils/cardStyles';
import ChipList from './ChipList';

/**
 * Builds a `Map` from a group's internal ID (what `subscribed_incidents` etc. actually
 * contains, see `eventTypes.js`) to its customer-facing ID, from a batch-fetched list
 * of full records. IDs missing a customer-facing value (still loading, or genuinely
 * not found) are simply absent from the map -- callers fall back to the raw internal
 * ID via `customIdMap.get(id) || id`.
 */
const buildCustomIdMap = (records, idField, customIdField) => {
    const map = new Map();
    (records || []).forEach((record) => {
        const internalId = record?.[idField];
        const customId = record?.[customIdField];
        if (internalId && customId) map.set(internalId, customId);
    });
    return map;
};

/**
 * Read-only "Event Subscriptions" section of the review modal -- one `Card` per
 * non-empty group of subscribed incident/maintenance/advisory IDs, each rendered as
 * clickable PANWDS `Tag` chips via `ChipList` (matching the region chips elsewhere in
 * the same modal) that navigate to that event's detail page.
 *
 * `subscribed_incidents`/`subscribed_maintenances`/`subscribed_advisories` only ever
 * contain the internal Jira-style ID (see `eventTypes.js`), not the customer-facing ID
 * the Incident/Maintenance lookup pages are keyed by in the URL. So each non-empty
 * group is resolved with one batched (comma-joined) fetch -- mirroring the existing
 * comma-joined filter convention for product_type/status/severity/continent/country/
 * city in `useIncidentFilters.js` -- rather than one request per chip.
 *
 * The three event types are fixed, so the three `useFetchX` hooks below are called
 * explicitly (never in a loop/conditionally) with `isEnabled` gating which ones
 * actually issue a request -- looping over `EVENT_TYPES` to call hooks dynamically
 * would violate the rules of hooks.
 *
 * `expandedEvents` / `onToggleExpand` are keyed by the `EVENT_TYPES` key (e.g.
 * "Incident"), which cannot collide with the product-type keys `expandedProducts` also
 * stores in `StatusPage.jsx`, so the same map is safely shared across both.
 *
 * `onNavigate` is called after a chip navigation is triggered, letting the parent
 * (`ManageSubscriptionsModal`) close itself so it doesn't float over the newly-navigated
 * tab content.
 */
const EventSubscriptionsSection = ({
    subscribedIncidents,
    subscribedMaintenances,
    subscribedAdvisories,
    expandedEvents,
    onToggleExpand,
    onNavigate,
}) => {
    const navigate = useNavigate();

    const incidentIds = subscribedIncidents || [];
    const maintenanceIds = subscribedMaintenances || [];
    const advisoryIds = subscribedAdvisories || [];

    const { data: incidentRecords } = useFetchIncidents({
        query: `${EVENT_TYPES.Incident.idField}=${incidentIds.join(',')}`,
        isEnabled: incidentIds.length > 0,
    });
    const { data: maintenanceRecords } = useFetchMaintenances({
        query: `${EVENT_TYPES.Maintenance.idField}=${maintenanceIds.join(',')}`,
        isEnabled: maintenanceIds.length > 0,
    });
    const { data: advisoryRecords } = useFetchAdvisories({
        query: `${EVENT_TYPES.Advisory.idField}=${advisoryIds.join(',')}`,
        isEnabled: advisoryIds.length > 0,
    });

    const incidentCustomIdMap = useMemo(
        () => buildCustomIdMap(incidentRecords, EVENT_TYPES.Incident.idField, EVENT_TYPES.Incident.customIdField),
        [incidentRecords],
    );
    const maintenanceCustomIdMap = useMemo(
        () => buildCustomIdMap(maintenanceRecords, EVENT_TYPES.Maintenance.idField, EVENT_TYPES.Maintenance.customIdField),
        [maintenanceRecords],
    );
    const advisoryCustomIdMap = useMemo(
        () => buildCustomIdMap(advisoryRecords, EVENT_TYPES.Advisory.idField, EVENT_TYPES.Advisory.customIdField),
        [advisoryRecords],
    );

    // Navigates using the resolved customer-facing ID when available, falling back to
    // the raw internal ID -- IncidentLookupPage/MaintenanceLookupPage/AdvisoryLookupPage
    // all accept either format, so this is safe even before resolution completes.
    const handleNavigate = useCallback((routeBase, targetId) => {
        navigate(`${routeBase}/${encodeURIComponent(targetId)}`);
        onNavigate?.();
    }, [navigate, onNavigate]);

    const groups = useMemo(() => {
        const groupData = {
            Incident: { ids: incidentIds, customIdMap: incidentCustomIdMap },
            Maintenance: { ids: maintenanceIds, customIdMap: maintenanceCustomIdMap },
            Advisory: { ids: advisoryIds, customIdMap: advisoryCustomIdMap },
        };
        return Object.entries(EVENT_TYPES)
            .map(([key, config]) => ({ key, config, ...groupData[key] }))
            .filter((group) => group.ids.length > 0);
    }, [incidentIds, maintenanceIds, advisoryIds, incidentCustomIdMap, maintenanceCustomIdMap, advisoryCustomIdMap]);

    if (groups.length === 0) return null;

    return (
        <div className='ssmutw-ml-0 ssmutw-mt-10'>
            <h3 className='ssmutw-pt-2 ssmutw-mb-4 ssmutw-text-base ssmutw-font-semibold'>Event Subscriptions</h3>
            <div className='ssmutw-flex ssmutw-flex-col ssmutw-gap-2'>
                {groups.map(({ key, config, ids, customIdMap }) => (
                    <div className='ssmutw-p-1' key={key}>
                        <Card shadow={false} addClassName={BORDERED_CARD_CLASS}>
                            <CardBody>
                                <Body size="sm" as="h4" addClassName="ssmutw-font-semibold ssmutw-mb-2">{config.pluralLabel}</Body>
                                <div className='ssmutw-flex ssmutw-flex-wrap ssmutw-items-center ssmutw-gap-1 md:ssmutw-gap-2'>
                                    <ChipList
                                        items={ids}
                                        isExpanded={!!expandedEvents[key]}
                                        onToggleExpand={() => onToggleExpand(key)}
                                        testIdPrefix={`review-event-chip-${key.toLowerCase()}`}
                                        formatLabel={(id) => customIdMap.get(id) || id}
                                        getTooltipLabel={() => `View this ${config.label.toLowerCase()}`}
                                        onItemClick={(id) => handleNavigate(config.routeBase, customIdMap.get(id) || id)}
                                    />
                                </div>
                            </CardBody>
                        </Card>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default React.memo(EventSubscriptionsSection);
